<template>
    <div class="superbets__container">
        <SuperBetSlider
            v-if="!isMarketGroupActive"
            :superbets="displayedOdds"
            :selectedOdds="selectedOdds"
            :isEventLocked="isEventLocked"
            :isResults="isResults"
            @oddClick="$emit('oddClick', $event)"
            @showMore="$emit('navigateToMarketGroup')"
        />
        <template v-else>
            <div
                v-if="isOddsRangeSliderVisible"
                class="superbets__filter"
            >
                <div class="superbets__slider">
                    <div class="superbets__slider-title">{{ $t('superBets.oddsRange') }}</div>
                    <OddsRangeSlider
                        v-model="range"
                        :odds="oddValues"
                        :oddsRangeValues="oddsRangeValues"
                        class="e2e-super-bets-range-filter"
                    />
                </div>
                <MarketTypesFilter
                    v-if="isMarketTypeFilterVisible"
                />
            </div>

            <InfiniteScroller
                class="superbets__cards-grid-container"
                @bottomReached="showMoreSuperbets"
            >
                <div class="superbets__number-of-selections">
                    {{ $tc('superBets.selections', potentialVisibleOdds.length) }}
                </div>

                <template v-if="slotIndex">
                    <SuperBet
                        v-for="odd in displayedOdds.slice(0, slotIndex)"
                        :key="odd.uuid"
                        :odd="odd"
                        :isSelected="selectedOdds.has(odd.uuid)"
                        :isEventLocked="isEventLocked"
                        :isResults="isResults"
                        @oddClick="$emit('oddClick', odd)"
                    />
                </template>

                <slot />

                <SuperBet
                    v-for="odd in displayedOdds.slice(slotIndex || 0)"
                    :key="odd.uuid"
                    :odd="odd"
                    :isSelected="selectedOdds.has(odd.uuid)"
                    :isEventLocked="isEventLocked"
                    :isResults="isResults"
                    @oddClick="$emit('oddClick', odd)"
                />
            </InfiniteScroller>
        </template>
    </div>
</template>
<script>
    import { mapGetters } from 'vuex';

    import { fallbackSuperBetsConfig } from '@modules/eventGrid/components/markets/constants';
    import InfiniteScroller from '@shared/components/InfiniteScroller.vue';

    import MarketTypesFilter from './MarketTypesFilter.vue';
    import SuperBet from './SuperBet.vue';
    import SuperBetSlider from './SuperBetSlider.vue';
    import {
        filterVisibleSuperBets,
        getFeaturedSuperBets,
        isMarketTypeFilterVisible,
        isOddsRangeSliderVisible,
        isShowMoreVisible,
    } from '../helpers';

    const oddsShownStep = 10;

    export default {
        name: 'SuperBetsMarket',
        props: {
            isMarketGroupActive: {
                type: Boolean,
                required: true,
            },
            odds: {
                type: Array,
                required: true,
            },
            market: {
                type: Object,
                required: true,
            },
            eventId: {
                type: Number,
                required: true,
            },
            isEventLocked: {
                type: Boolean,
                required: true,
            },
            oddValueDynamics: {
                type: Object,
                required: false,
                default: undefined,
            },
            isOngoing: {
                type: Boolean,
                required: true,
            },
            selectedOdds: {
                type: Set,
                required: true,
            },
            hideSuperBetsFilter: {
                type: Boolean,
                required: false,
                default: false,
            },
            isResults: {
                type: Boolean,
                required: false,
                default: false,
            },
            slotIndex: {
                type: Number,
                required: false,
                default: null,
            },
        },
        emits: [
            'oddClick',
            'navigateToMarketGroup',
            'heightUpdated',
        ],
        data() {
            return {
                range: [],
                oddsShown: oddsShownStep,
            };
        },
        computed: {
            ...mapGetters('data/country', {
                serverConfig: 'config',
            }),
            numberOfFeaturedSuperbets() {
                return this.serverConfig.superbets.numberOfFeaturedSuperbets || fallbackSuperBetsConfig.numberOfFeaturedSuperbets;
            },
            oddValues() {
                return this.odds.map((odd) => odd.value);
            },
            potentialVisibleOdds() {
                if (!this.isMarketGroupActive) {
                    return getFeaturedSuperBets(this.odds, this.numberOfFeaturedSuperbets);
                } else {
                    return this.filteredOdds;
                }
            },
            displayedOdds() {
                return this.potentialVisibleOdds.slice(0, this.oddsShown);
            },
            isOddsRangeSliderVisible() {
                if (this.hideSuperBetsFilter) {
                    return false;
                }

                return isOddsRangeSliderVisible(
                    this.odds,
                    this.numberOfFeaturedSuperbets,
                );
            },
            isMarketTypeFilterVisible() {
                return isMarketTypeFilterVisible(
                    this.odds,
                    {
                        isEnabled: this.serverConfig.superbets.isMarketTypesFilterEnabled,
                        numberOfFeaturedSuperBets: this.numberOfFeaturedSuperbets,
                        availableBetTypesInStruct: 0,
                        numberOfBetTypes: 0,
                        minimalNumberOfBetTypes: 3,
                    },
                );
            },
            isShowMoreVisible() {
                return isShowMoreVisible(
                    this.odds,
                    this.numberOfFeaturedSuperbets,
                );
            },
            oddsRangeValues() {
                return this.serverConfig.superbets.oddsRangeValues || fallbackSuperBetsConfig.oddsRangeValues;
            },
            filteredOdds() {
                const [from, to] = this.range;
                const maxOddsRangeValue = this.oddsRangeValues[this.oddsRangeValues.length - 1];
                return filterVisibleSuperBets(
                    this.odds,
                    from,
                    to,
                    maxOddsRangeValue,
                    { isOddsRangeSliderVisible: this.isOddsRangeSliderVisible },
                );
            },
        },
        watch: {
            displayedOdds(value, oldValue) {
                if (value.length !== oldValue.length) {
                    this.$emit('heightUpdated');
                }
            },
            oddsRangeValues: {
                handler(value) {
                    const start = value[0];
                    const end = value[value.length - 1];
                    this.range = [start, end];
                },
                immediate: true,
            },
        },
        methods: {
            showMoreSuperbets() {
                if (this.oddsShown < this.potentialVisibleOdds.length) {
                    this.oddsShown += oddsShownStep;
                }
            },
        },
        destroyed() {
            this.featureSubscriptionUnsubscribeFn?.();
        },
        components: {
            SuperBet,
            SuperBetSlider,
            MarketTypesFilter,
            InfiniteScroller,
            OddsRangeSlider: () => import('./OddsRangeSlider/OddsRangeSlider.vue'),
        },
    };
</script>
